const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL ||
  (typeof window === "undefined"
    ? "http://localhost:2000"
    : `${window.location.protocol}//${window.location.hostname}:2000`);

interface ApiResponse<T> {
  code: number;
  msg: string;
  data: T;
}

export class ApiError extends Error {
  status: number;
  code?: number;

  constructor(message: string, status: number, code?: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

type AuthFailureHandler = (status: 401 | 403, message: string) => void;

let authFailureHandler: AuthFailureHandler | null = null;

export function setAuthFailureHandler(handler: AuthFailureHandler | null) {
  authFailureHandler = handler;
}

/**
 * 检查存储的 token 是否存在（实际有效性由 /verifyToken 校验）
 */
export function isTokenValid(token: string | null): boolean {
  return !!token && token.length > 10;
}

export function getStoredToken(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  return localStorage.getItem("token") || localStorage.getItem("adminToken");
}

export async function apiRequest<T>(
  endpoint: string,
  options?: RequestInit,
  notifyAuthFailure = true
): Promise<ApiResponse<T>> {
  const url = `${API_BASE_URL}${endpoint}`;
  const response = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });

  let responseText = "";
  let invalidData = false;
  let data: Partial<ApiResponse<T>> = {};
  try {
    responseText = await response.text();
    if (responseText.trim()) {
      const parsed: unknown = JSON.parse(responseText);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        invalidData = true;
      } else {
        data = parsed as Partial<ApiResponse<T>>;
      }
    }
  } catch {
    invalidData = true;
  }

  // HTTP authentication status remains authoritative even if a proxy returns
  // HTML, malformed JSON, or a body that cannot be read.
  if (response.status === 401) {
    const message = typeof data.msg === "string" && data.msg || "登录已失效，请重新登录";
    if (notifyAuthFailure) authFailureHandler?.(401, message);
    throw new ApiError(message, 401, data.code);
  }

  if (response.status === 403) {
    const message = typeof data.msg === "string" && data.msg || "无权限访问";
    if (notifyAuthFailure) authFailureHandler?.(403, message);
    throw new ApiError(message, 403, data.code);
  }

  if (invalidData) {
    throw new ApiError(`服务器返回了无效数据（HTTP ${response.status}）`, response.status);
  }

  if (!response.ok) {
    throw new ApiError(
      data.msg || `HTTP error! status: ${response.status}`,
      response.status,
      data.code
    );
  }

  if (!responseText.trim()) {
    throw new ApiError(`服务器返回空响应（HTTP ${response.status}）`, response.status);
  }

  return data as ApiResponse<T>;
}

export async function apiRequestWithAuth<T>(
  endpoint: string,
  token: string,
  options?: RequestInit,
  notifyAuthFailure = true
): Promise<ApiResponse<T>> {
  return apiRequest<T>(endpoint, {
    ...options,
    headers: {
      ...options?.headers,
      Authorization: `Bearer ${token}`,
    },
  }, notifyAuthFailure);
}
